import { useMemo, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateBR, formatDateOnly } from "@/utils/dateUtils";
import {
  useAgenciaTimeline,
  type AgenciaCargaDiariaPonto,
  type AgenciaProjetoGantt,
  type AgenciaVagaTimeline,
  type PeriodoTimeline,
} from "@/hooks/useAgenciaTimeline";
import type { TimelineEvento } from "@/types/timeline";
import { TimelineLane, type LaneSegmento } from "@/components/timeline/TimelineLane";
import { TimelineCounterLane } from "@/components/timeline/TimelineCounterLane";
import { EventDetailDialog } from "@/components/timeline/EventDetailDialog";
import { PeriodoToggle } from "@/components/timeline/PeriodoToggle";
import { vagaStatusBarClass, vagaStatusLabel } from "./agenciaVagaStatus";

const LIMITE_LINHAS_PADRAO = 5;
const TRUNCAR_LABEL = 18;

function truncar(texto: string, limite: number): string {
  if (texto.length <= limite) return texto;
  return `${texto.slice(0, limite - 1)}…`;
}

function formatMesCurto(ms: number) {
  return new Date(ms).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
}

/** Mostra as linhas "em aberto" sempre; completa até o limite com as mais recentes
 *  encerradas; o resto some atrás do "mostrar mais". */
function linhasVisiveis<T>(itens: T[], estaAberto: (item: T) => boolean, mostrarTodas: boolean): T[] {
  if (mostrarTodas || itens.length <= LIMITE_LINHAS_PADRAO) return itens;
  const abertos = itens.filter(estaAberto);
  if (abertos.length >= LIMITE_LINHAS_PADRAO) return abertos;
  const encerrados = itens.filter((item) => !estaAberto(item));
  const faltam = LIMITE_LINHAS_PADRAO - abertos.length;
  return [...abertos, ...encerrados.slice(-faltam)];
}

function tooltipVaga(vaga: AgenciaVagaTimeline) {
  const passos = vaga.transicoes.map((transicao) => vagaStatusLabel(transicao.status_destino));
  return (
    <div className="space-y-0.5 text-xs">
      <div className="font-medium">{vaga.titulo}</div>
      <div className="text-muted-foreground">Etapa atual: {vagaStatusLabel(vaga.status_atual)}</div>
      <div className="text-muted-foreground">Criada em {formatDateOnly(vaga.criada_em)}</div>
      {vaga.terminal_em && (
        <div className="text-muted-foreground">Concluída em {formatDateOnly(vaga.terminal_em)}</div>
      )}
      {passos.length > 0 && (
        <div className="text-muted-foreground">Passou por: {passos.join(", ")}</div>
      )}
    </div>
  );
}

function tooltipGantt(projeto: AgenciaProjetoGantt) {
  const renovacoes = projeto.n_renovacoes ?? 0;
  return (
    <div className="space-y-0.5 text-xs">
      <div className="font-medium">{projeto.freelancer_nome}</div>
      <div className="text-muted-foreground">
        {projeto.ini ? formatDateBR(projeto.ini) : "sem início registrado"} até{" "}
        {projeto.aberto ? "hoje" : projeto.fim ? formatDateBR(projeto.fim) : "sem conclusão registrada"}
      </div>
      <div className="text-muted-foreground">
        {renovacoes > 0
          ? `${renovacoes} ${renovacoes === 1 ? "renovação" : "renovações"}`
          : "sem renovação"}
      </div>
    </div>
  );
}

interface AgenciaTimelineProps {
  agenciaId: string;
}

/**
 * Aba "Linha do tempo" da agência (Propostas/timeline-agencia-plano.md §5/§6):
 * atividade (contador), vagas (barras de estado), seleção (eventos ligados
 * por vaga), projetos (mini-gantt), avaliações (eventos bidirecionais) e
 * radar (colapsado por padrão). Uma chamada de RPC (get_agencia_timeline)
 * alimenta a aba inteira.
 */
export function AgenciaTimeline({ agenciaId }: AgenciaTimelineProps) {
  const [periodo, setPeriodo] = useState<PeriodoTimeline>("24m");
  const [mostrarVagasEncerradas, setMostrarVagasEncerradas] = useState(false);
  const [mostrarProjetosEncerrados, setMostrarProjetosEncerrados] = useState(false);
  const [mostrarRadar, setMostrarRadar] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState<TimelineEvento | null>(null);
  const [dialogAberto, setDialogAberto] = useState(false);

  const { data, isLoading, isError, refetch } = useAgenciaTimeline(agenciaId, periodo);

  const eventos = useMemo(() => data?.eventos ?? [], [data]);
  const vagas = useMemo(() => data?.vagas ?? [], [data]);
  const projetosGantt = useMemo(() => data?.projetos_gantt ?? [], [data]);
  const cargaDiaria = useMemo(() => data?.carga_diaria ?? [], [data]);
  const avaliacoesInternas = data?.avaliacoes_internas ?? [];

  // Janela do gráfico: períodos fixos contam a partir de hoje; "tudo" abre até
  // o acontecimento mais antigo entre eventos, vagas, gantt e carga diária.
  const janela = useMemo(() => {
    const fimMs = Date.now();
    if (periodo !== "tudo") {
      const inicio = new Date();
      inicio.setMonth(inicio.getMonth() - (periodo === "12m" ? 12 : 24));
      return { fromMs: inicio.getTime(), toMs: fimMs };
    }
    const candidatos: number[] = [];
    for (const evento of eventos) {
      const occurredMs = new Date(evento.occurred_at).getTime();
      if (!Number.isNaN(occurredMs)) candidatos.push(occurredMs);
    }
    for (const vaga of vagas) candidatos.push(new Date(vaga.criada_em).getTime());
    for (const projeto of projetosGantt) {
      if (projeto.ini) candidatos.push(new Date(projeto.ini).getTime());
    }
    if (cargaDiaria.length) candidatos.push(new Date(cargaDiaria[0].dia).getTime());
    if (!candidatos.length) {
      const fallback = new Date();
      fallback.setMonth(fallback.getMonth() - 24);
      return { fromMs: fallback.getTime(), toMs: fimMs };
    }
    return { fromMs: Math.min(...candidatos), toMs: fimMs };
  }, [periodo, eventos, vagas, projetosGantt, cargaDiaria]);

  const nowPercent = 100; // a janela sempre termina em "hoje"

  const eventosSelecao = useMemo(() => eventos.filter((evento) => evento.lane === "vagas"), [eventos]);
  const eventosAvaliacoes = useMemo(
    () => eventos.filter((evento) => evento.lane === "avaliacoes"),
    [eventos],
  );
  const eventosRadar = useMemo(
    () => eventos.filter((evento) => evento.lane === "relacionamento"),
    [eventos],
  );

  const vagasOrdenadas = useMemo(
    () => [...vagas].sort((a, b) => new Date(a.criada_em).getTime() - new Date(b.criada_em).getTime()),
    [vagas],
  );
  const vagasVisiveis = linhasVisiveis(vagasOrdenadas, (vaga) => !vaga.terminal_em, mostrarVagasEncerradas);
  const vagasOcultas = vagasOrdenadas.length - vagasVisiveis.length;

  const projetosOrdenados = useMemo(
    () => [...projetosGantt].sort((a, b) => new Date(a.ini ?? 0).getTime() - new Date(b.ini ?? 0).getTime()),
    [projetosGantt],
  );
  const projetosVisiveis = linhasVisiveis(
    projetosOrdenados,
    (projeto) => projeto.aberto,
    mostrarProjetosEncerrados,
  );
  const projetosOcultos = projetosOrdenados.length - projetosVisiveis.length;

  const contadorPontos = useMemo(
    () =>
      cargaDiaria.map((ponto: AgenciaCargaDiariaPonto) => ({
        dia: ponto.dia,
        valor: ponto.freelancers_ativos,
      })),
    [cargaDiaria],
  );

  const abrirEvento = (evento: TimelineEvento) => {
    setEventoSelecionado(evento);
    setDialogAberto(true);
  };

  const temAlgumDado =
    eventos.length > 0 || vagas.length > 0 || projetosGantt.length > 0 || avaliacoesInternas.length > 0;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-40" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-muted-foreground">Não foi possível carregar a linha do tempo.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  if (!temAlgumDado) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <PeriodoToggle periodo={periodo} onChange={setPeriodo} />
        </div>
        <p className="py-6 text-center text-sm text-muted-foreground">
          Sem histórico registrado ainda com esta agência.
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <PeriodoToggle periodo={periodo} onChange={setPeriodo} />
        </div>

        <div className="space-y-2">
          {/* Eixo de tempo alinhado às barras (espaçador do tamanho do rótulo) */}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="w-[104px] shrink-0" aria-hidden />
            <div className="flex flex-1 justify-between">
              <span>{formatMesCurto(janela.fromMs)}</span>
              <span>hoje</span>
            </div>
          </div>

          {/* 1. Atividade (contador de freelancers ativos por dia) */}
          <TimelineCounterLane
            label="Atividade"
            windowFromMs={janela.fromMs}
            windowToMs={janela.toMs}
            nowPercent={nowPercent}
            pontos={contadorPontos}
            unidadeLabel="freelancers ativos"
            formatarDia={formatDateBR}
          />

          {/* 2. Vagas (uma barra por vaga: criação até status terminal ou hoje) */}
          {vagasVisiveis.length > 0 && (
            <div className="space-y-1 pt-1">
              {vagasVisiveis.map((vaga) => {
                const segmento: LaneSegmento = {
                  fromMs: new Date(vaga.criada_em).getTime(),
                  toMs: vaga.terminal_em ? new Date(vaga.terminal_em).getTime() : null,
                  className: vagaStatusBarClass(vaga.status_atual),
                  tooltip: tooltipVaga(vaga),
                };
                return (
                  <TimelineLane
                    key={vaga.vaga_id}
                    label={truncar(vaga.titulo, TRUNCAR_LABEL)}
                    laneKey="vagas"
                    windowFromMs={janela.fromMs}
                    windowToMs={janela.toMs}
                    nowPercent={nowPercent}
                    segments={[segmento]}
                  />
                );
              })}
              {vagasOcultas > 0 && (
                <div className="pl-[112px]">
                  <Button variant="ghost" size="sm" onClick={() => setMostrarVagasEncerradas(true)}>
                    Mostrar todas as vagas ({vagasOcultas} a mais)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* 3. Seleção (marcos do kanban: selecionado/shortlist/aprovado/preterido/reprovado) */}
          <TimelineLane
            label="Seleção"
            laneKey="vagas"
            windowFromMs={janela.fromMs}
            windowToMs={janela.toMs}
            nowPercent={nowPercent}
            eventos={eventosSelecao}
            connectBy={(evento) => evento.vaga_id}
            onEventoClick={abrirEvento}
          />

          {/* 4. Projetos (mini-gantt: uma barra por cadeia de renovação) */}
          {projetosVisiveis.length > 0 && (
            <div className="space-y-1 pt-1">
              {projetosVisiveis.map((projeto) => {
                const segmento: LaneSegmento = {
                  fromMs: projeto.ini ? new Date(projeto.ini).getTime() : janela.fromMs,
                  toMs: projeto.aberto || !projeto.fim ? null : new Date(projeto.fim).getTime(),
                  className: "bg-indigo-500",
                  tooltip: tooltipGantt(projeto),
                };
                return (
                  <TimelineLane
                    key={projeto.chain_id}
                    label={truncar(projeto.freelancer_nome, TRUNCAR_LABEL)}
                    laneKey="projetos"
                    windowFromMs={janela.fromMs}
                    windowToMs={janela.toMs}
                    nowPercent={nowPercent}
                    segments={[segmento]}
                  />
                );
              })}
              {projetosOcultos > 0 && (
                <div className="pl-[112px]">
                  <Button variant="ghost" size="sm" onClick={() => setMostrarProjetosEncerrados(true)}>
                    Mostrar todos os projetos ({projetosOcultos} a mais)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* 5. Avaliações (bidirecional: dadas pela agência e recebidas dela) */}
          <TimelineLane
            label="Avaliações"
            laneKey="avaliacoes"
            windowFromMs={janela.fromMs}
            windowToMs={janela.toMs}
            nowPercent={nowPercent}
            eventos={eventosAvaliacoes}
            onEventoClick={abrirEvento}
          />

          {/* 6. Radar (colapsado por padrão) */}
          {eventosRadar.length > 0 && (
            <>
              {mostrarRadar ? (
                <TimelineLane
                  label="Radar"
                  laneKey="relacionamento"
                  windowFromMs={janela.fromMs}
                  windowToMs={janela.toMs}
                  nowPercent={nowPercent}
                  eventos={eventosRadar}
                  onEventoClick={abrirEvento}
                />
              ) : (
                <div className="pl-[112px]">
                  <Button variant="ghost" size="sm" onClick={() => setMostrarRadar(true)}>
                    Mostrar indicações do radar ({eventosRadar.length})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <EventDetailDialog
          evento={eventoSelecionado}
          open={dialogAberto}
          onOpenChange={setDialogAberto}
        />
      </div>
    </TooltipProvider>
  );
}

export default AgenciaTimeline;
